import React from "react";
import { SubmissionStatus } from "@/types/fitconnect.ts";
import { reformatDate } from "@/lib/dateTime.tsx";

function badgeFor(status: SubmissionStatus): {
  label: string;
  className: string;
} {
  if (status.wasRejected)
    return { label: "Abgelehnt", className: "badge-error" };
  if (status.wasAccepted)
    return { label: "Angenommen", className: "badge-success" };

  switch (status.state) {
    case null:
      return { label: "Unbekannt", className: "badge-neutral" };
    case "Incomplete":
      return { label: "Unvollständig", className: "badge-warning" };
    case "Forwarded":
      return { label: "Abgeholt", className: "badge-accent" };
    default:
      return { label: "In Zustellung", className: "badge-info" };
  }
}

/**
 * Shows whether the Fachverfahren has accepted or rejected a submission, and why — read via
 * GET /api/fitconnect/submissions/{id}/status, i.e. the SDK's GetSubmissionLog.
 */
export function SubmissionStatusBadge({
  status,
  isLoading,
}: {
  status: SubmissionStatus | null;
  isLoading: boolean;
}) {
  if (isLoading) {
    return <span className="loading loading-dots loading-sm" />;
  }
  if (!status) return null;

  const { label, className } = badgeFor(status);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`badge ${className}`}>{label}</span>
        {status.state && (
          <span className="text-xs text-slate-500">({status.state})</span>
        )}
      </div>
      <div className="text-sm text-slate-600">
        {status.submittedAt && (
          <div>Eingereicht: {reformatDate(status.submittedAt)}</div>
        )}
        {status.decidedAt && (
          <div>Entschieden: {reformatDate(status.decidedAt)}</div>
        )}
      </div>
      {status.problems.length > 0 && (
        <ul className="list-disc pl-5 text-sm text-error">
          {status.problems.map((p, i) => (
            <li key={`${p.type}-${i}`}>
              <span className="font-medium">{p.title}</span>
              {p.detail && p.detail !== p.title && <>: {p.detail}</>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
